import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Ticket, Package, Settings, AlertTriangle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function AdminBillingSettings() {
  const [coupons, setCoupons] = useState([]);
  const [addons, setAddons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Coupon Dialog
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: -1,
    valid_from: '',
    valid_until: '',
    applicable_packages: [],
    min_billing_cycle: '',
    is_active: true
  });

  // Add-on Dialog
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonForm, setAddonForm] = useState({
    name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    feature_key: '',
    feature_value: '',
    is_active: true
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [couponsRes, addonsRes, settingsRes, packagesRes] = await Promise.all([
        api.get('/admin/coupons'),
        api.get('/admin/add-ons'),
        api.get('/admin/billing-settings'),
        api.get('/admin/packages')
      ]);
      setCoupons(couponsRes.data.coupons || []);
      setAddons(addonsRes.data.add_ons || []);
      setSettings(settingsRes.data.settings || {});
      setPackages(packagesRes.data.packages || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  // ============== COUPONS ==============

  const handleSaveCoupon = async () => {
    try {
      if (editingCoupon) {
        await api.put(`/admin/coupons/${editingCoupon}`, couponForm);
        toast({ title: 'Berhasil', description: 'Kupon berhasil diupdate' });
      } else {
        await api.post('/admin/coupons', couponForm);
        toast({ title: 'Berhasil', description: 'Kupon berhasil dibuat' });
      }
      setShowCouponDialog(false);
      setEditingCoupon(null);
      resetCouponForm();
      fetchAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan', variant: 'destructive' });
    }
  };

  const resetCouponForm = () => {
    setCouponForm({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      max_uses: -1,
      valid_from: '',
      valid_until: '',
      applicable_packages: [],
      min_billing_cycle: '',
      is_active: true
    });
  };

  const editCoupon = (coupon) => {
    setEditingCoupon(coupon.id);
    setCouponForm({ ...coupon });
    setShowCouponDialog(true);
  };

  const deleteCoupon = async (id) => {
    if (!confirm('Yakin ingin menonaktifkan kupon ini?')) return;
    await api.delete(`/admin/coupons/${id}`);
    toast({ title: 'Berhasil', description: 'Kupon dinonaktifkan' });
    fetchAll();
  };

  // ============== ADD-ONS ==============

  const handleSaveAddon = async () => {
    try {
      const data = {
        ...addonForm,
        feature_value: isNaN(Number(addonForm.feature_value)) ? addonForm.feature_value : Number(addonForm.feature_value)
      };
      if (editingAddon) {
        await api.put(`/admin/add-ons/${editingAddon}`, data);
        toast({ title: 'Berhasil', description: 'Add-on berhasil diupdate' });
      } else {
        await api.post('/admin/add-ons', data);
        toast({ title: 'Berhasil', description: 'Add-on berhasil dibuat' });
      }
      setShowAddonDialog(false);
      setEditingAddon(null);
      resetAddonForm();
      fetchAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan', variant: 'destructive' });
    }
  };

  const resetAddonForm = () => {
    setAddonForm({
      name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      feature_key: '',
      feature_value: '',
      is_active: true
    });
  };

  const editAddon = (addon) => {
    setEditingAddon(addon.id);
    setAddonForm({ ...addon });
    setShowAddonDialog(true);
  };

  const deleteAddon = async (id) => {
    if (!confirm('Yakin ingin menonaktifkan add-on ini?')) return;
    await api.delete(`/admin/add-ons/${id}`);
    toast({ title: 'Berhasil', description: 'Add-on dinonaktifkan' });
    fetchAll();
  };

  // ============== SETTINGS ==============

  const handleSaveSettings = async () => {
    try {
      await api.put('/admin/billing-settings', settings);
      toast({ title: 'Berhasil', description: 'Pengaturan berhasil disimpan' });
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menyimpan', variant: 'destructive' });
    }
  };

  const runSuspendCheck = async () => {
    try {
      const res = await api.post('/admin/run-suspend-check');
      toast({ 
        title: 'Proses Selesai', 
        description: `Suspended: ${res.data.suspended}, Masuk grace period: ${res.data.entered_grace_period}` 
      });
      fetchAll();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menjalankan proses', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-billing-settings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Pengaturan Billing SaaS</h1>
          <p className="text-sm text-muted-foreground">Kelola kupon, add-on, dan pengaturan billing</p>
        </div>
      </div>

      <Tabs defaultValue="coupons">
        <TabsList>
          <TabsTrigger value="coupons"><Ticket className="h-4 w-4 mr-2" />Kupon ({coupons.length})</TabsTrigger>
          <TabsTrigger value="addons"><Package className="h-4 w-4 mr-2" />Add-Ons ({addons.length})</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="h-4 w-4 mr-2" />Pengaturan</TabsTrigger>
        </TabsList>

        {/* COUPONS TAB */}
        <TabsContent value="coupons" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingCoupon(null); resetCouponForm(); setShowCouponDialog(true); }} data-testid="add-coupon-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Kupon
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {coupons.map((coupon) => (
              <Card key={coupon.id} className={!coupon.is_active ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-mono">{coupon.code}</CardTitle>
                    <Badge variant={coupon.is_active ? 'default' : 'secondary'}>
                      {coupon.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p className="text-2xl font-bold text-emerald-600">
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `Rp ${fmt(coupon.discount_value)}`}
                    </p>
                    <p className="text-muted-foreground">
                      Digunakan: {coupon.used_count || 0} / {coupon.max_uses === -1 ? '∞' : coupon.max_uses}
                    </p>
                    {coupon.valid_until && (
                      <p className="text-muted-foreground">
                        Berlaku sampai: {new Date(coupon.valid_until).toLocaleDateString('id-ID')}
                      </p>
                    )}
                    {coupon.min_billing_cycle && (
                      <Badge variant="outline" className="text-xs">
                        Min: {coupon.min_billing_cycle === 'yearly' ? 'Tahunan' : 'Bulanan'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => editCoupon(coupon)}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteCoupon(coupon.id)}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {coupons.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-muted-foreground">
                  Belum ada kupon. Klik "Tambah Kupon" untuk membuat.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ADD-ONS TAB */}
        <TabsContent value="addons" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingAddon(null); resetAddonForm(); setShowAddonDialog(true); }} data-testid="add-addon-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Add-On
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addons.map((addon) => (
              <Card key={addon.id} className={!addon.is_active ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{addon.name}</CardTitle>
                    <Badge variant={addon.is_active ? 'default' : 'secondary'}>
                      {addon.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{addon.description}</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-xl font-bold">Rp {fmt(addon.price_monthly)}<span className="text-muted-foreground font-normal">/bln</span></p>
                    <p className="text-muted-foreground">Rp {fmt(addon.price_yearly)}/thn</p>
                    <Badge variant="outline" className="mt-2">
                      {addon.feature_key}: {addon.feature_value}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => editAddon(addon)}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteAddon(addon.id)}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {addons.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-muted-foreground">
                  Belum ada add-on. Klik "Tambah Add-On" untuk membuat.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="space-y-6">
          {/* TRIAL SETTINGS */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-500" />
                Free Trial
              </CardTitle>
              <p className="text-sm text-muted-foreground">Atur masa uji coba gratis untuk tenant baru — durasi & paket mana yang diberikan.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Aktifkan Free Trial</Label>
                  <p className="text-sm text-muted-foreground">Tenant baru otomatis dapat masa trial saat mendaftar</p>
                </div>
                <Switch
                  checked={settings.trial_enabled !== false}
                  onCheckedChange={(v) => setSettings({ ...settings, trial_enabled: v })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Durasi Trial (Hari)</Label>
                  <Input
                    type="number"
                    value={settings.trial_days ?? 14}
                    onChange={(e) => setSettings({ ...settings, trial_days: parseInt(e.target.value) || 0 })}
                    className="w-32"
                  />
                  <p className="text-sm text-muted-foreground mt-1">Misal 14 = "Gratis 14 hari" di landing page</p>
                </div>
                <div>
                  <Label>Paket Selama Trial</Label>
                  <Select
                    value={settings.trial_package_id || 'auto'}
                    onValueChange={(v) => setSettings({ ...settings, trial_package_id: v === 'auto' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Pilih paket" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Otomatis (paket default trial)</SelectItem>
                      {packages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} — Rp {fmt(p.price_monthly)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-1">Fitur paket ini aktif selama masa trial</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <Label>Saat Trial Habis</Label>
                  <Select
                    value={settings.trial_expiry_action || 'downgrade'}
                    onValueChange={(v) => setSettings({ ...settings, trial_expiry_action: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="downgrade">Turunkan ke paket lain (toko tetap jalan)</SelectItem>
                      <SelectItem value="suspend">Suspend (toko off sampai upgrade)</SelectItem>
                      <SelectItem value="readonly">Read-only (tampil tapi tak bisa edit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.trial_expiry_action === 'downgrade' && (
                  <div>
                    <Label>Turunkan ke Paket</Label>
                    <Select
                      value={settings.trial_downgrade_package_id || 'auto'}
                      onValueChange={(v) => setSettings({ ...settings, trial_downgrade_package_id: v === 'auto' ? '' : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Pilih paket" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Otomatis (paket termurah / Free)</SelectItem>
                        {packages.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name} — Rp {fmt(p.price_monthly)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button onClick={handleSaveSettings}>Simpan Pengaturan Trial</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Grace Period & Auto Suspend
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto Suspend Aktif</Label>
                  <p className="text-sm text-muted-foreground">Tenant akan di-suspend otomatis setelah grace period habis</p>
                </div>
                <Switch 
                  checked={settings.auto_suspend_enabled || false}
                  onCheckedChange={(v) => setSettings({ ...settings, auto_suspend_enabled: v })}
                />
              </div>
              <div>
                <Label>Grace Period (Hari)</Label>
                <Input 
                  type="number" 
                  value={settings.grace_period_days || 7}
                  onChange={(e) => setSettings({ ...settings, grace_period_days: parseInt(e.target.value) || 7 })}
                  className="w-32"
                />
                <p className="text-sm text-muted-foreground mt-1">Waktu tambahan setelah langganan expired sebelum suspend</p>
              </div>
              <div>
                <Label>Reminder Sebelum Expired (Hari)</Label>
                <Input 
                  value={(settings.reminder_days || [7, 3, 1]).join(', ')}
                  onChange={(e) => setSettings({ ...settings, reminder_days: e.target.value.split(',').map(d => parseInt(d.trim())).filter(Boolean) })}
                  placeholder="7, 3, 1"
                />
                <p className="text-sm text-muted-foreground mt-1">Kirim reminder H-7, H-3, H-1 sebelum expired</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveSettings}>Simpan Pengaturan</Button>
                <Button variant="outline" onClick={runSuspendCheck}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Jalankan Suspend Check
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* COUPON DIALOG */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Kupon' : 'Tambah Kupon Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kode Kupon</Label>
              <Input 
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="DISKON10"
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipe Diskon</Label>
                <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Persentase (%)</SelectItem>
                    <SelectItem value="fixed">Nominal Tetap (Rp)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nilai Diskon</Label>
                <Input 
                  type="number"
                  value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Berlaku Dari</Label>
                <Input 
                  type="date"
                  value={couponForm.valid_from ? couponForm.valid_from.split('T')[0] : ''}
                  onChange={(e) => setCouponForm({ ...couponForm, valid_from: e.target.value })}
                />
              </div>
              <div>
                <Label>Berlaku Sampai</Label>
                <Input 
                  type="date"
                  value={couponForm.valid_until ? couponForm.valid_until.split('T')[0] : ''}
                  onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Penggunaan</Label>
                <Input 
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: parseInt(e.target.value) || -1 })}
                  placeholder="-1 = Unlimited"
                />
              </div>
              <div>
                <Label>Min Billing Cycle</Label>
                <Select value={couponForm.min_billing_cycle || 'any'} onValueChange={(v) => setCouponForm({ ...couponForm, min_billing_cycle: v === 'any' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Semua</SelectItem>
                    <SelectItem value="monthly">Bulanan</SelectItem>
                    <SelectItem value="yearly">Tahunan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={couponForm.is_active}
                onCheckedChange={(v) => setCouponForm({ ...couponForm, is_active: v })}
              />
              <Label>Kupon Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponDialog(false)}>Batal</Button>
            <Button onClick={handleSaveCoupon}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD-ON DIALOG */}
      <Dialog open={showAddonDialog} onOpenChange={setShowAddonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAddon ? 'Edit Add-On' : 'Tambah Add-On Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Add-On</Label>
              <Input 
                value={addonForm.name}
                onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                placeholder="Extra Storage 10GB"
              />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Input 
                value={addonForm.description}
                onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                placeholder="Tambahan storage untuk file produk"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Harga Bulanan (Rp)</Label>
                <Input 
                  type="number"
                  value={addonForm.price_monthly}
                  onChange={(e) => setAddonForm({ ...addonForm, price_monthly: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Harga Tahunan (Rp)</Label>
                <Input 
                  type="number"
                  value={addonForm.price_yearly}
                  onChange={(e) => setAddonForm({ ...addonForm, price_yearly: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Feature Key</Label>
                <Select value={addonForm.feature_key || 'custom'} onValueChange={(v) => setAddonForm({ ...addonForm, feature_key: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="extra_products">Extra Products</SelectItem>
                    <SelectItem value="extra_orders">Extra Orders</SelectItem>
                    <SelectItem value="extra_storage">Extra Storage</SelectItem>
                    <SelectItem value="extra_domains">Extra Domains</SelectItem>
                    <SelectItem value="extra_staff">Extra Staff</SelectItem>
                    <SelectItem value="whitelabel">Whitelabel</SelectItem>
                    <SelectItem value="api_access">API Access</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Feature Value</Label>
                <Input 
                  value={addonForm.feature_value}
                  onChange={(e) => setAddonForm({ ...addonForm, feature_value: e.target.value })}
                  placeholder="100 atau 10GB"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={addonForm.is_active}
                onCheckedChange={(v) => setAddonForm({ ...addonForm, is_active: v })}
              />
              <Label>Add-On Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddonDialog(false)}>Batal</Button>
            <Button onClick={handleSaveAddon}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
